import { c as _c } from "react/compiler-runtime";
import classNames from 'classnames';
import isWithinInterval from 'date-fns/isWithinInterval';
import Button from '@helios/Button';
import Card from '@helios/Card';
import PageHero from '@helios/PageHero';
import TermBanner from '../TermBanner.js';
import StartAdmissionTasks from './StartAdmissionTasks.js';
import useCampus from '@/hooks/useCampus.ts';
import { useMagicLinkRedirect } from '@/hooks/useMagicLinkRedirect';
import { APPLICATION_HERO } from './applicationHero';
import { REVIEW_STEP, programStepForApplication, stepProgressPercent } from './programStep';
import Spinner from '@helios/Spinner';
import { FormattedBrandLogo } from '../FormattedBrandLogo.js';
import RegularArrowRightFromBracketIcon from '@/components/icons/regular/arrow-right-from-bracket';
import styles from './ApplicationForm.module.scss';
function ApplicationStatus(t0) {
  const $ = _c(33);
  const {
    app,
    degrees,
    term,
    onClick
  } = t0;
  let t1;
  if ($[0] !== app) {
    t1 = stepProgressPercent(app.form.step ?? 0, programStepForApplication(app).variant).toFixed();
    $[0] = app;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const progress = t1;
  const pending = app.form.step === REVIEW_STEP;
  let t2;
  if ($[2] !== term) {
    t2 = term ? new Date() >= new Date(term.applyEndsAt) : false;
    $[2] = term;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const expired = t2;
  let decision = "Pending";
  if (app.decisionNotification) {
    if (app.finalDecision === 1) {
      decision = "Admitted";
    } else {
      if (app.finalDecision > 1) {
        decision = "Application not accepted";
      }
    }
  }
  let t3;
  if ($[4] !== app.degreeId || $[5] !== degrees) {
    t3 = degrees.find(d => d.id === app.degreeId)?.name;
    $[4] = app.degreeId;
    $[5] = degrees;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const degreeName = t3;
  const state = pending ? decision === "Admitted" ? "admitted" : decision === "Application not accepted" ? "not-accepted" : "submitted" : expired ? "expired" : "in-progress";
  let t4;
  if ($[7] !== expired || $[8] !== onClick || $[9] !== pending) {
    t4 = event => {
      if (!pending && !expired) {
        onClick(event);
      }
    };
    $[7] = expired;
    $[8] = onClick;
    $[9] = pending;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  const handleOpen = t4;
  const t5 = term ? `${term.seasonValue} Application` : "Pending Application";
  let t6;
  if ($[11] !== t5) {
    t6 = <div className={styles.status__title}>{t5}</div>;
    $[11] = t5;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== expired || $[14] !== handleOpen || $[15] !== pending || $[16] !== progress) {
    t7 = !pending && <div className={styles.status__row}><span className={styles.status__meta}>{progress}% completed</span><div className={classNames(styles.progress__track, styles.status__progress)}><div className={styles.progress__bar} role="progressbar" style={{
          "--progress": `${progress}%`
        }} aria-valuenow={progress} aria-valuemin="0" aria-valuemax="100" aria-label={`${progress}% completed`} /></div>{!expired ? <Button arrow={true} onClick={handleOpen}>Continue</Button> : <Button disabled={true}>Expired</Button>}</div>;
    $[13] = expired;
    $[14] = handleOpen;
    $[15] = pending;
    $[16] = progress;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  let t8;
  if ($[18] !== decision || $[19] !== degreeName || $[20] !== pending) {
    t8 = pending && <div className={styles.status__column}>{degreeName && <span className={styles.status__meta}>Program: {degreeName}</span>}<span className={styles.status__meta}>Decision:{" "}<span className={classNames(styles.badge, {
          [styles["badge--admit"]]: decision === "Admitted",
          [styles["badge--reject"]]: decision === "Application not accepted"
        })}>{decision}</span></span><Button disabled={true}>Submitted</Button></div>;
    $[18] = decision;
    $[19] = degreeName;
    $[20] = pending;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  let t9;
  if ($[22] !== t6 || $[23] !== t7 || $[24] !== t8) {
    t9 = <>{t6}{t7}{t8}</>;
    $[22] = t6;
    $[23] = t7;
    $[24] = t8;
    $[25] = t9;
  } else {
    t9 = $[25];
  }
  const content = t9;
  const t10 = !pending && !expired;
  let t11;
  if ($[26] !== t10) {
    t11 = classNames(styles.status, {
      [styles["status--open"]]: t10
    });
    $[26] = t10;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  let t12;
  if ($[28] !== content || $[29] !== handleOpen || $[30] !== state || $[31] !== t11) {
    t12 = <Card padded="md" surface="subtle" className={t11} data-application-state={state} onClick={handleOpen}>{content}</Card>;
    $[28] = content;
    $[29] = handleOpen;
    $[30] = state;
    $[31] = t11;
    $[32] = t12;
  } else {
    t12 = $[32];
  }
  return t12;
}
function ApplicationFormWelcome(t0) {
  const $ = _c(68);
  const {
    site,
    apps,
    terms,
    employer,
    onApp,
    onLogout,
    showTermBanner: t1
  } = t0;
  const showTermBanner = t1 === undefined ? true : t1;
  const sortedApps = [...apps].sort(_temp);
  const {
    allDegreesQuery
  } = useCampus();
  let t2;
  if ($[0] !== apps) {
    t2 = apps.some(_temp2);
    $[0] = apps;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const hasCompletedApp = t2;
  let t3;
  if ($[2] !== hasCompletedApp) {
    t3 = {
      enabled: hasCompletedApp
    };
    $[2] = hasCompletedApp;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const magicLinkQuery = useMagicLinkRedirect(t3);
  let t4;
  if ($[4] !== terms) {
    t4 = terms.filter(_temp3);
    $[4] = terms;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const currentTerm = t4[0];
  let t5;
  if ($[6] !== currentTerm?.id) {
    t5 = a_1 => a_1.termId === currentTerm?.id;
    $[6] = currentTerm?.id;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const currentTermApp = sortedApps.find(t5);
  const pending = currentTermApp?.form.step === 6;
  let t6;
  if ($[8] !== currentTerm?.id) {
    t6 = a_2 => a_2.termId !== currentTerm?.id;
    $[8] = currentTerm?.id;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const otherTermApps = sortedApps.filter(t6);
  let t7;
  if ($[10] !== apps || $[11] !== terms) {
    let t8;
    if ($[13] !== apps) {
      t8 = t_0 => !apps?.some(a_3 => a_3.termId === t_0.id) && isWithinInterval(new Date(), {
        start: new Date(t_0.applyStartsAt),
        end: new Date(t_0.applyEndsAt)
      });
      $[13] = apps;
      $[14] = t8;
    } else {
      t8 = $[14];
    }
    t7 = terms.filter(t8);
    $[10] = apps;
    $[11] = terms;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  const availableTerms = t7;
  const loading = !allDegreesQuery.isSuccess;
  if (loading) {
    let t8;
    if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
      t8 = <div className={styles.loader}><Spinner /></div>;
      $[15] = t8;
    } else {
      t8 = $[15];
    }
    return t8;
  }
  let t8;
  if ($[16] !== showTermBanner) {
    t8 = showTermBanner && <TermBanner />;
    $[16] = showTermBanner;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  const T0 = PageHero;
  const t9 = PageHero;
  const t10 = "full";
  let t11;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = classNames(styles["photo-split"], styles["photo-split--sticky-media"]);
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  let t12;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <PageHero.Image priority={true} src={APPLICATION_HERO.image} alt="Continue your application" frame="portrait" />;
    $[19] = t12;
  } else {
    t12 = $[19];
  }
  let t13;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <PageHero.Media placement="split">{t12}<PageHero.Caption><p>{APPLICATION_HERO.graduateName}</p><p><small>Campus Graduate</small></p></PageHero.Caption></PageHero.Media>;
    $[20] = t13;
  } else {
    t13 = $[20];
  }
  const t14 = PageHero;
  const t15 = "split";
  const t16 = styles;
  let t17;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <RegularArrowRightFromBracketIcon />;
    $[21] = t17;
  } else {
    t17 = $[21];
  }
  let t18;
  if ($[22] !== onLogout) {
    t18 = <Button iconLeft={t17} onClick={() => onLogout()} size="md" tone="neutral" variant="outline">Log out</Button>;
    $[22] = onLogout;
    $[23] = t18;
  } else {
    t18 = $[23];
  }
  const t19 = hasCompletedApp ? !magicLinkQuery.isSuccess && !magicLinkQuery.isError : !availableTerms.length;
  let t20;
  if ($[24] !== hasCompletedApp || $[25] !== magicLinkQuery || $[26] !== onApp) {
    t20 = () => {
      if (hasCompletedApp) {
        if (magicLinkQuery.isError || !magicLinkQuery.data) {
          magicLinkQuery.refetch();
          return;
        }
        window.open(magicLinkQuery.data, "_blank", "noopener");
        return;
      }
      onApp("new");
    };
    $[24] = hasCompletedApp;
    $[25] = magicLinkQuery;
    $[26] = onApp;
    $[27] = t20;
  } else {
    t20 = $[27];
  }
  const t21 = hasCompletedApp ? magicLinkQuery.isError ? "Try again" : "Go to your portal" : "New application";
  let t22;
  if ($[28] !== hasCompletedApp || $[29] !== t19 || $[30] !== t20 || $[31] !== t21) {
    t22 = <Button disabled={t19} arrow={hasCompletedApp} onClick={t20} size="md" tone="brand" variant="solid">{t21}</Button>;
    $[28] = hasCompletedApp;
    $[29] = t19;
    $[30] = t20;
    $[31] = t21;
    $[32] = t22;
  } else {
    t22 = $[32];
  }
  let t23;
  if ($[33] !== t18 || $[34] !== t22) {
    t23 = <header className={styles.intro__header}>{t18}{t22}</header>;
    $[33] = t18;
    $[34] = t22;
    $[35] = t23;
  } else {
    t23 = $[35];
  }
  let t24;
  if ($[36] !== employer || $[37] !== site) {
    t24 = <FormattedBrandLogo site={site} employer={employer} align="start" />;
    $[36] = employer;
    $[37] = site;
    $[38] = t24;
  } else {
    t24 = $[38];
  }
  let t25;
  if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = classNames(styles["screen-title"], styles["screen-title--spaced"]);
    $[39] = t25;
  } else {
    t25 = $[39];
  }
  let t26;
  if ($[40] !== apps[0].form.firstName) {
    t26 = <h1 className={t25}>Welcome back, {apps[0].form.firstName}.</h1>;
    $[40] = apps[0].form.firstName;
    $[41] = t26;
  } else {
    t26 = $[41];
  }
  let t27;
  if ($[42] !== pending) {
    t27 = pending && <StartAdmissionTasks />;
    $[42] = pending;
    $[43] = t27;
  } else {
    t27 = $[43];
  }
  const t28 = !!currentTermApp && <div className={styles["term-group"]}><p className={styles["term-label"]}>Current term</p><ApplicationStatus app={currentTermApp} degrees={allDegreesQuery.data} term={currentTerm} onClick={e => onApp(currentTermApp.id)} /></div>;
  const t29 = !!otherTermApps.length && <div className={styles["term-group"]}><p className={styles["term-label"]}>Other terms</p>{otherTermApps.map(app => <ApplicationStatus key={app.id} app={app} degrees={allDegreesQuery.data} term={terms.find(t_1 => t_1.id === app.termId)} onClick={e_0 => onApp(app.id)} />)}</div>;
  let t30;
  if ($[44] !== t16.intro || $[45] !== t23 || $[46] !== t24 || $[47] !== t26 || $[48] !== t27 || $[49] !== t28 || $[50] !== t29) {
    t30 = <div className={t16.intro}>{t23}{t24}{t26}{t27}{t28}{t29}</div>;
    $[44] = t16.intro;
    $[45] = t23;
    $[46] = t24;
    $[47] = t26;
    $[48] = t27;
    $[49] = t28;
    $[50] = t29;
    $[51] = t30;
  } else {
    t30 = $[51];
  }
  let t31;
  if ($[52] !== t14.Content || $[53] !== t30) {
    t31 = <t14.Content placement={t15}>{t30}</t14.Content>;
    $[52] = t14.Content;
    $[53] = t30;
    $[54] = t31;
  } else {
    t31 = $[54];
  }
  let t32;
  if ($[55] !== t11 || $[56] !== t13 || $[57] !== t31) {
    t32 = <div className={t11}>{t13}{t31}</div>;
    $[55] = t11;
    $[56] = t13;
    $[57] = t31;
    $[58] = t32;
  } else {
    t32 = $[58];
  }
  let t33;
  if ($[59] !== t32 || $[60] !== t9.Container) {
    t33 = <t9.Container width={t10}>{t32}</t9.Container>;
    $[59] = t32;
    $[60] = t9.Container;
    $[61] = t33;
  } else {
    t33 = $[61];
  }
  let t34;
  if ($[62] !== T0 || $[63] !== t33) {
    t34 = <T0>{t33}</T0>;
    $[62] = T0;
    $[63] = t33;
    $[64] = t34;
  } else {
    t34 = $[64];
  }
  let t35;
  if ($[65] !== t34 || $[66] !== t8) {
    t35 = <>{t8}{t34}</>;
    $[65] = t34;
    $[66] = t8;
    $[67] = t35;
  } else {
    t35 = $[67];
  }
  return t35;
}
function _temp3(t) {
  return new Date(t.applyEndsAt) > new Date();
}
function _temp2(a_0) {
  return a_0.form?.step === 6;
}
function _temp(a, b) {
  return new Date(b.createdAt) - new Date(a.createdAt);
}
export default ApplicationFormWelcome;